import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { FaHeart, FaEye, FaRegComment, FaUser, FaBars, FaTimes } from "react-icons/fa";
import "../styles/BlogPost.css"; 

// Update this path to match your actual logo file location
import Logo1 from "../pictures/Logo2.PNG"; 

const API = "https://backend.afrivisionconsultants.com";
// const API_URL = "http://localhost:5000";

// --- INTERNAL NAVBAR COMPONENT ---
const BlogNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="blog-navbar">
      <div className="nav-container">
        {/* Logo Image */}
        <Link to="/" className="nav-logo">
           <img src={Logo1} alt="AfriVision" className="nav-logo-img" />
        </Link>

        {/* Mobile Toggle */}
        <div className="menu-icon" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </div>

        {/* Links */}
        <div className={`nav-links ${isOpen ? "active" : ""}`}>
          <Link to="/" className="nav-link" onClick={() => setIsOpen(false)}>Home</Link>
          <Link to="/blog" className="nav-link" onClick={() => setIsOpen(false)}>Blog</Link>
          <Link to="/contact" className="nav-btn" onClick={() => setIsOpen(false)}>Get in Touch</Link>
        </div>
      </div>
    </nav>
  );
};

// --- MAIN POST COMPONENT ---
const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Interaction State
  const [likes, setLikes] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [commentForm, setCommentForm] = useState({ name: "", content: "" });
  const [submissionStatus, setSubmissionStatus] = useState(""); 

  // REF to prevent double-fetching in React Strict Mode
  const dataFetchedRef = useRef(false);

  useEffect(() => {
    // If we have already fetched data for this render cycle, stop.
    if (dataFetchedRef.current) return;
    dataFetchedRef.current = true;

    const fetchPostData = async () => {
      try {
        // 1. UNIQUE VIEW LOGIC
        const viewedKey = `viewed_${slug}`;
        const hasViewed = localStorage.getItem(viewedKey);
        
        // Only ask server to increment if user hasn't viewed this specific post yet
        let url = `${API}/public/blog/post/${slug}`;
        if (!hasViewed) {
          url += "?increment=true";
        }

        const res = await fetch(url);
        if (!res.ok) throw new Error("Post not found");
        
        const data = await res.json();
        setPost(data);
        setLikes(data.likes || 0);

        // Mark as viewed in local storage so it doesn't count again for this user
        if (!hasViewed) {
          localStorage.setItem(viewedKey, "true");
        }

        // 2. CHECK IF ALREADY LIKED
        const likedPosts = JSON.parse(localStorage.getItem("liked_posts") || "[]");
        if (likedPosts.includes(data.id)) {
          setHasLiked(true);
        }

      } catch (err) {
        console.error("Failed to load post", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPostData();
  }, [slug]);

// ... inside BlogPost component ...

  const handleLike = async () => {
    if (!post || hasLiked) return; 

    setLikes(prev => prev + 1);
    setHasLiked(true);

    const likedPosts = JSON.parse(localStorage.getItem("liked_posts") || "[]");
    if (!likedPosts.includes(post.id)) {
        likedPosts.push(post.id);
        localStorage.setItem("liked_posts", JSON.stringify(likedPosts));
    }

    try {
      // This now connects to the new route in Server.js
      await fetch(`${API}/public/blog/like`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postId: post.id })
      });
    } catch (error) {
      console.error("Error liking post:", error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentForm.name || !commentForm.content) return;

    setSubmissionStatus("sending");

    try {
      const res = await fetch(`${API}/public/blog/comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          postId: post.id,
          authorName: commentForm.name,
          content: commentForm.content
        })
      });

      if (res.ok) {
        setSubmissionStatus("success");
        setCommentForm({ name: "", content: "" }); 
        
        // Refresh post to see new comment (re-fetch logic simplified for comments)
        const refreshRes = await fetch(`${API}/public/blog/post/${slug}`);
        const refreshData = await refreshRes.json();
        setPost(refreshData);

        setTimeout(() => setSubmissionStatus(""), 3000);
      } else {
        setSubmissionStatus("error");
      }
    } catch (error) {
      setSubmissionStatus("error");
    }
  };

  if (loading) return <div className="blog-section" style={{textAlign:"center", padding: "50px"}}>Loading post...</div>;
  if (!post) return <div className="blog-section" style={{textAlign:"center", padding: "50px"}}>Post not found.</div>;

  return (
    <>
      <BlogNavbar />

      <div className="blog-section single-post-view">
        <div className="single-post-header">
          <h1>{post.title}</h1>
          
          <div className="meta-row">
              <span style={{display: "flex", alignItems: "center", gap: "5px"}}>
                  {new Date(post.scheduledAt).toLocaleDateString()}
              </span>
              
              {post.BlogCategory && <span className="category-tag">{post.BlogCategory.name}</span>}
              
              <span className="meta-divider">|</span>
              
              <span className="meta-stat" title="Views">
                  <FaEye /> {post.views}
              </span>
              
              <span className="meta-stat" title="Likes">
                  <FaHeart style={{ color: likes > 0 ? "#e74c3c" : "inherit" }} /> {likes}
              </span>
          </div>
        </div>

        <div 
          className="post-content"
          dangerouslySetInnerHTML={{ __html: post.content }} 
        />

        <hr className="divider" />

        <div className="interaction-bar">
          <button 
            className={`like-btn ${hasLiked ? "liked" : ""}`} 
            onClick={handleLike}
            disabled={hasLiked}
            title={hasLiked ? "You already liked this post" : "Like this post"}
          >
              <FaHeart /> {likes} Likes
          </button>
          <span style={{color: "#777", fontSize: "0.95rem"}}>
              {hasLiked ? "Thanks for liking!" : "Enjoyed this article? Give it a like!"}
          </span>
        </div>

        <div className="comments-section">
          <h3><FaRegComment /> Comments ({post.BlogComments ? post.BlogComments.length : 0})</h3>
          
          <div className="comment-list">
            {post.BlogComments && post.BlogComments.length > 0 ? (
              post.BlogComments.map(c => (
                <div key={c.id} className={`comment ${c.isAdminReply ? "admin-reply" : ""}`}>
                  <div style={{display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px"}}>
                      <div style={{
                          width: "30px", height: "30px", background: "#ddd", borderRadius: "50%", 
                          display: "flex", alignItems: "center", justifyContent: "center", color: "#666"
                      }}>
                          <FaUser size={14} />
                      </div>
                      <strong>{c.isAdminReply ? "Admin Team" : c.authorName}</strong>
                  </div>
                  <p>{c.content}</p>
                </div>
              ))
            ) : (
              <p className="no-comments">No comments yet. Be the first to share your thoughts!</p>
            )}
          </div>

          {post.allowComments ? (
              <div className="comment-form-wrapper">
                  <h4 style={{marginTop: 0, marginBottom: "15px"}}>Leave a Reply</h4>
                  <form onSubmit={handleCommentSubmit} className="comment-form">
                      <input 
                          type="text" 
                          placeholder="Your Name" 
                          value={commentForm.name} 
                          onChange={e => setCommentForm({...commentForm, name: e.target.value})}
                          required
                          className="form-control"
                      />
                      <textarea 
                          placeholder="Write your comment here..." 
                          rows="4"
                          value={commentForm.content}
                          onChange={e => setCommentForm({...commentForm, content: e.target.value})}
                          required
                          className="form-control"
                      ></textarea>
                      
                      <button type="submit" className="submit-btn" disabled={submissionStatus === "sending"}>
                          {submissionStatus === "sending" ? "Posting..." : "Post Comment"}
                      </button>
                      
                      {submissionStatus === "success" && (
                          <p className="success-msg" style={{color: "green", marginTop: "10px", fontWeight: "600"}}>
                              Comment posted successfully!
                          </p>
                      )}
                      {submissionStatus === "error" && (
                          <p className="error-msg" style={{color: "red", marginTop: "10px"}}>
                              Failed to post comment. Please try again.
                          </p>
                      )}
                  </form>
              </div>
          ) : (
              <div style={{padding: "20px", background: "#f9f9f9", borderRadius: "8px", textAlign: "center", color: "#777"}}>
                  <p>Comments are closed for this post.</p>
              </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogPost;